import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Card,
  CardHeader,
  CardSection,
  CondensedSmall,
  DateUtil,
  Flashy,
  Form,
  Grid,
  GridColumn,
  Gutter,
  Layout,
  LayoutItem,
  Paragraph,
  SmallHeader,
  Toggle,
  useApi,
} from '@axiom/ui';
import { isLpUser } from '@axiom/utils';
import { CandidateSchema } from '@axiom/validation';

import { CandidateApi } from '../../api/candidate-api';
import { UserApi } from '../../api/user-api';
import { PreferencesNavigation } from '../PreferencesNavigation/PreferencesNavigation';
import { CandidateUtil } from '../../utils/candidate-util';
import { PreferencesUtil } from '../../utils/preferences-util';
import { CandidateProfileIssuesApi } from '../../api/candidate-profile-issues-api';
import { FeatureVisibilityUtil } from '../../utils/feature-visibility-util';

import { PreferencesBanner } from './PreferencesBanner';

const EditWorkStylePreferencesSchema = CandidateSchema.partial().pick({
  isOpenToMultipleClients: true,
  isOpenToCommute: true,
  isOpenToOtherWork: true,
});

export const PreferencesFlexibility = () => {
  const navigate = useNavigate();

  const [{ data: user }] = useApi(UserApi.readUser());
  const { candidateId: urlCandidateId } = useParams();

  const candidateId = CandidateUtil.reconcileUserCandidateId(
    user,
    urlCandidateId
  );

  const [{ data: candidate }] = useApi(CandidateApi.readCandidate(candidateId));
  const { workStylePreferencesReviewedAt } = candidate;

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    if (
      FeatureVisibilityUtil.shouldSeeLimitedFeatureSetBasedOnProfileStatus(
        candidate
      )
    ) {
      void navigate('../communications');
    } else if (isLpUser(user)) {
      timeout = setTimeout(() => {
        void CandidateApi.updateCandidate(candidateId, {
          workStylePreferencesReviewedAt: DateUtil.formatAsTimestamp(
            new Date()
          ),
        }).then(async () =>
          Promise.all([
            CandidateApi.refreshCandidate(candidateId),
            CandidateProfileIssuesApi.refreshCandidateProfileIssues(
              candidateId
            ),
          ])
        );
      }, 500);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, []);

  return (
    <Grid name="WORK_STYLE_PREFERENCES">
      <GridColumn largeScreenWidth={4} smallScreenWidth={12}>
        <PreferencesNavigation />
        <Gutter only="smallScreen" bottom="16px" />
      </GridColumn>
      <GridColumn largeScreenWidth={8} smallScreenWidth={12}>
        {PreferencesUtil.shouldPreferenceNeedReview(
          workStylePreferencesReviewedAt
        ) && <PreferencesBanner />}
        <Form
          name="work-style-preferences-form"
          schema={EditWorkStylePreferencesSchema}
          onSubmit={async formData => {
            await CandidateApi.updateCandidate(candidateId, {
              ...CandidateSchema.partial().parse(formData),
              workStylePreferencesReviewedAt: DateUtil.formatAsTimestamp(
                new Date()
              ),
            });
            return Promise.all([
              CandidateApi.refreshCandidate(candidateId),
              CandidateProfileIssuesApi.refreshCandidateProfileIssues(
                candidateId
              ),
            ]);
          }}
          initialValues={{
            isOpenToMultipleClients: !!candidate.isOpenToMultipleClients,
            isOpenToCommute: !!candidate.isOpenToCommute,
            isOpenToOtherWork: !!candidate.isOpenToOtherWork,
          }}
          submitOnChange
        >
          {() => (
            <Card name="FLEXIBILITY_CARD">
              <CardHeader>
                <SmallHeader>Flexibility</SmallHeader>
              </CardHeader>
              <CardSection divider>
                <Layout
                  stackableOn="mobile"
                  horizontalGutter="24px"
                  position="stretch space-between"
                >
                  <LayoutItem>
                    <Paragraph>
                      <Flashy bold>
                        I’m open to working with more than one client at a time
                      </Flashy>
                    </Paragraph>
                    <Gutter bottom="16px" only="mobile" />
                  </LayoutItem>
                  <LayoutItem>
                    <Toggle name="isOpenToMultipleClients" />
                  </LayoutItem>
                </Layout>
              </CardSection>
              <CardSection divider>
                <Layout
                  stackableOn="mobile"
                  horizontalGutter="24px"
                  position="stretch space-between"
                >
                  <LayoutItem>
                    <Paragraph>
                      <Flashy bold>
                        I’m open to working onsite—either part-time or full-time
                      </Flashy>
                      <Gutter bottom="16px" />
                      Sometimes our clients request your presence at their
                      office(s) during the engagement.
                    </Paragraph>
                    <Gutter bottom="16px" only="mobile" />
                  </LayoutItem>
                  <LayoutItem>
                    <Toggle name="isOpenToCommute" />
                  </LayoutItem>
                </Layout>
              </CardSection>
              <CardSection divider>
                <Layout
                  stackableOn="mobile"
                  horizontalGutter="24px"
                  position="stretch space-between"
                >
                  <LayoutItem>
                    <Paragraph>
                      <Flashy bold>
                        I'm open to opportunities that don't exactly match my
                        work preferences
                      </Flashy>
                      <Gutter bottom="16px" />
                      We may suggest roles we believe you'd excel at, even if
                      they don't perfectly align with your stated preferences.
                    </Paragraph>
                    <Gutter bottom="16px" only="mobile" />
                  </LayoutItem>
                  <LayoutItem>
                    <Toggle name="isOpenToOtherWork" />
                  </LayoutItem>
                </Layout>
              </CardSection>
            </Card>
          )}
        </Form>
        {!!workStylePreferencesReviewedAt && (
          <Gutter top="10px">
            <CondensedSmall>
              <Flashy
                italic
                color="textSubtle"
                name="WORK_STYLE_PREFERENCES_REVIEWED_AT"
              >
                Last reviewed:{' '}
                {DateUtil.displayDateFullYearShortMonthDayName(
                  workStylePreferencesReviewedAt
                )}
              </Flashy>
            </CondensedSmall>
          </Gutter>
        )}
      </GridColumn>
    </Grid>
  );
};
